import { BaseEntity, GenericListEntity, UploadFile, AttachedFile, CompanyInfo } from '@base/entities/BaseEntities';
import { QueryList } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import camelcaseKeys from 'camelcase-keys';
import { Observable, Subject, take } from 'rxjs';

/** Utility functions for interaction with objects */
// @dynamic
export class Utils {

    /** Returns a new typed array */
    public static MapList<TEntity extends BaseEntity>(type: (new () => TEntity), Entities: TEntity[]): TEntity[] {
        //Empty response, do nothing
        if (Entities.length == 0) return undefined;

        //Map each element and add to array
        const newArray: Array<TEntity> = new Array<TEntity>();
        Entities.forEach(Entity => {
            newArray.push(this.MapObject<TEntity>(type, Entity));
        });

        return newArray;
    }

    public static MapAsNumeric(item: GenericListEntity): any {
        return (
            {
                Value: +item.Value,
                Text: item.Text,
                Description: item.Description
            });
    }

    /** Copy all properties from source object to a new typed object */
    /**
     * 
     * @param type The type of the result entity
     * @param Entity The value of the source 
     */
    public static MapObject<TEntity extends BaseEntity>(type: (new () => TEntity), Entity: TEntity): TEntity {
        //Empty response, do nothing
        if (Entity == undefined) return undefined;

        //Create new instance, copy 
        const newObject: TEntity = this.GetNewInstance(type);
        const resultObject = this.CloneObject(Entity as any) as TEntity;
        resultObject.TypeName = newObject.TypeName;
        return resultObject;
    }

    public static GetNewInstance<TEntity>(type: (new () => TEntity)): TEntity {
        return new type();
    }

    public static CloneObject(obj: object): any {
        return cloneDeep(obj);
    }

    public static IsPrimitiveType(obj: object) {
        return (obj !== Object(obj));
    }

    public static CopyProperties<TEntity extends BaseEntity>(type: (new () => TEntity), source: TEntity, destination: TEntity) {
        //Empty response, do nothing
        if (source == undefined || destination == undefined) return undefined;
        for (const k in source) destination[k] = source[k];
    }

    public static IsEmpty(obj: any): boolean {
        if (typeof (obj) === 'number' && obj === 0) return false;
        return obj === undefined || obj == null || obj == '';
    }

    public static NZ(obj: any, replace: any = ''): string {
        return (this.IsEmpty(obj) ? replace : obj);
    }

    public static ValEx(s: string): number {
        if (this.IsEmpty(s)) return 0;
        if (typeof s === 'number') return s;
        const num = s.replace(',', '').replace(' ', '');
        return +num;
    }

    //Computer checksum hash (not for cryptography)
    public static ComputeChecksum(value: string): string {
        let h1 = 0xdeadbeef ^ 0, h2 = 0x41c6ce57 ^ 0;
        for (let i = 0, ch; i < value.length; i++) {
            ch = value.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
        h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
        return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
    }

    //Convert value to bool, considering undefined
    public static ToBoolean(value: boolean): boolean {
        if (value == undefined) return false;
        return value;
    }

    //Convert 64base encoded data into blob
    public static ConvertToBlob(oBase64Data: any, sContentType: string, bIsUseUint16: boolean = false): Blob {
        if (oBase64Data === undefined || oBase64Data === null) return undefined;
        const byteCharacters = atob(oBase64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = !bIsUseUint16 ? new Uint8Array(byteNumbers) : new Uint16Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: sContentType });
    }

    public static ConvertToBase64(bytesArray: number[]) {
        let binary = '';
        const bytes = new Uint8Array(bytesArray);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    public static ConvertToBytesArray(base64Data: string) {
        if (base64Data === undefined || base64Data === null) return undefined;
        const data = base64Data.split(';');
        base64Data = data.length > 1 ? data[1].substring(data[1].indexOf(',') + 1) : base64Data;

        const binary_string = window.atob(base64Data);
        const len = binary_string.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return Array.from(new Uint8Array(bytes.buffer));
    }

    public static ConvertStringToBase64(str: string) {
        const bytes = new TextEncoder().encode(str);
        const binString = Array.from(bytes, (byte: number) =>
            String.fromCodePoint(byte),
        ).join('');

        return window.btoa(binString);
    }

    public static GetImageSource(bytesArray: number[], mimeType: string): string {
        return `data:${mimeType};base64,${this.ConvertToBase64(bytesArray)}`;
    }

    public static GetImageSourceFromBase64(base64String: string, mimeType: string): string {
        return `data:${mimeType};base64,${base64String}`;
    }

    public static ExtractDataFromDataURL(DataURL: string): string {
        return DataURL.split(',')[1];
    }

    public static CombineChild<T>(oItem1: T, oItem2: T): T {
        return oItem1 != undefined ? oItem1 : oItem2;
    }

    public static CombineChildren<T>(list1: QueryList<T>, list2: QueryList<T>): T[] {
        let arr: T[] = [];
        if (list1 != undefined) arr = arr.concat(list1.toArray());
        if (list2 != undefined) arr = arr.concat(list2.toArray());
        return arr;
    }

    public static ConvertCSVToJSONArray(strData, sDelimited: string) {
        let data = [], i = 0, k = 0, header = [];


        // Create a regular expression to parse the CSV values.
        const objPattern = new RegExp(
            (
                // Delimiters.
                '(\\' + sDelimited + '|\\r?\\n|\\r|^)' +

                // Quoted fields.
                '(?:"([^"]*(?:""[^"]*)*)"|' +

                // Standard fields.
                '([^"\\' + sDelimited + '\\r\\n]*))'
            ),
            'gi'
        );

        // Create an array to hold our data. Give the array
        // a default empty first row.
        let arrData = [[]];

        // Create an array to hold our individual pattern
        // matching groups.
        let arrMatches = null;


        // Keep looping over the regular expression matches
        // until we can no longer find a match.
        while (arrMatches = objPattern.exec(strData)) {

            // Get the delimiter that was found.
            const strMatchedDelimiter = arrMatches[1];

            // Check to see if the given delimiter has a length
            // (is not the start of string) and if it matches
            // field delimiter. If id does not, then we know
            // that this delimiter is a row delimiter.
            if (strMatchedDelimiter.length && (strMatchedDelimiter != sDelimited)) {
                // Since we have reached a new row of data,
                // add an empty row to our data array.
                arrData.push([]);
            }

            // Now that we have our delimiter out of the way,
            // let's check to see which kind of value we
            // captured (quoted or unquoted).
            if (arrMatches[2]) {
                // We found a quoted value. When we capture
                // this value, unescape any double quotes.
                var strMatchedValue = arrMatches[2].replace(new RegExp('""', 'g'), '"');

            } else {
                // We found a non-quoted value.
                var strMatchedValue = arrMatches[3];
            }

            // Now that we have our value string, let's add
            // it to the data array.
            arrData[arrData.length - 1].push(strMatchedValue);
        }


        //Filter out empty lines
        arrData = arrData.filter(x => !this.IsEmpty(x));

        //Rename headers
        for (i = 0; i < arrData.length; i++) {
            if (i == 0) {
                header = arrData[i];
            } else {
                const obj = {};
                for (k = 0; k < header.length; k++) {
                    if (k < arrData[i].length) {
                        obj[header[k]] = arrData[i][k];
                    }
                }
                data.push(obj);
            }
        }
        return data;

    }

    public static ContainsHebrewLetters(str: any): boolean {
        const regex = new RegExp('[א-ת]+');
        return (regex.test(str));
    }

    public static ContainsEnglishOnly(str: any): boolean {
        const regex = new RegExp('[^a-zA-Z\\s]');
        return (!regex.test(str));
    }

    public static LoadFilesBody(files: File[], done: (uploads: UploadFile[]) => void) {
        const result: UploadFile[] = [];

        if (files && files.length) {
            const length = files.length;
            let count = 0;

            for (const file of files) {
                const fileReader = new FileReader();

                fileReader.onloadend = (e) => {
                    count++;

                    const buffer = fileReader.result as ArrayBuffer;
                    const array = Array.from(new Uint8Array(buffer));

                    result.push({
                        Name: file.name,
                        Type: file.type,
                        Data: array,
                        Buffer: buffer,
                        File: file
                    } as UploadFile);

                    if (length === count) {
                        done(result);
                    }

                };
                fileReader.readAsArrayBuffer(file);
            }
        } else {
            done(result);
        }
    }

    public static DoFileDownload(file: AttachedFile | string, fileBlob: string, contentType: string, bIsUseUint16: boolean = false) {
        const blob = Utils.ConvertToBlob(fileBlob, contentType, bIsUseUint16);
        const objectUrl = URL.createObjectURL(blob);
        const fileName = typeof file === 'string' ? file : (file.Name + (file.Extension ? '.' + file.Extension : ''));
        this.DownloadFile(objectUrl, fileName);
    }

    public static DownloadFile(fileUrl: string, fileName: string) {
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(fileUrl);
    }

    // Compare not undefined/null objects
    public static IsEquals(obj1: {}, obj2: {}, ignoreEmptyProps: boolean = false) {
        if (obj1 === null || obj2 === null || !this.IsObject(obj1) || !this.IsObject(obj2)) return false;
        return this.StringifyObject(obj1, ignoreEmptyProps) === this.StringifyObject(obj2, ignoreEmptyProps);
    }

    public static ConvertBlobToImage(image: Blob, result: (image) => void) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            result(reader.result);
        }, false);

        if (image && image.size !== 0) {
            reader.readAsDataURL(image);
        } else {
            result(null);
        }
    }

    public static ConvertBlobToBytesArray(file: Blob, result: (bytes) => void) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            const arrayBuffer = (reader.result as ArrayBuffer);
            result(Array.from(new Uint8Array(arrayBuffer)));
        }, false);

        if (file && file.size !== 0) {
            reader.readAsArrayBuffer(file);
        } else {
            result(null);
        }
    }

    public static ConvertToBytesArrayObservable(file: Blob): Observable<number[]> {
        const convertedBytesSubject = new Subject<number[]>();
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            const arrayBuffer = (reader.result as ArrayBuffer);
            convertedBytesSubject.next(Array.from(new Uint8Array(arrayBuffer)));
        }, false);

        if (file && file.size !== 0) {
            reader.readAsArrayBuffer(file);
        } else {
            convertedBytesSubject.next(undefined);
        }

        return convertedBytesSubject.asObservable().pipe(take(1));
    }

    public static UniqueBy(array: any[], mapFunc: Function = x => x) {
        return new Set(array.map(x => mapFunc(x)));
    }

    public static LoadJsonFile<T>(path: string): T {
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.open('GET', path, false);
        if (xmlhttp.overrideMimeType) {
            xmlhttp.overrideMimeType('application/json');
        }
        xmlhttp.send();
        const json = xmlhttp.status == 200 && !xmlhttp.responseText.startsWith('<') ? xmlhttp.responseText : null;
        return json ? JSON.parse(json) : null;
    }

    // current implementation doesn't allow to have routes with numbers inside
    //public static getClearURLFromRoute(event) {
    //    const route = event.urlAfterRedirects.split('?')[0]
    //        .replace(/[0-9]/g, '')
    //        .replace(/\/$/, '')
    //        .replace('(', '')
    //        .replace(')', '');
    //    return route.substring(0, route.indexOf('_') - 1) + route.substring(route.lastIndexOf('_') + 1);
    //}

    public static ToCamelCase<T extends Record<string, any> | readonly any[]>(input: T): T {
        return camelcaseKeys<T>(input, { deep: true }) as T;
    }

    public static ParseJSONString(jsonString: string): any {
        return JSON.parse(jsonString, this.dateParser);
    }

    private static _reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
    private static _reMsAjax = /^\/Date\((d|-|.*)\)[\/|\\]$/;
    private static dateParser = (key, value) => {
        if (typeof value === 'string') {
            let a = Utils._reISO.exec(value);
            if (a)
                return new Date(value);
            a = Utils._reMsAjax.exec(value);
            if (a) {
                const b = a[1].split(/[-+,.]/);
                return new Date(b[0] ? +b[0] : 0 - +b[1]);
            }
        }
        return value;
    };

    public static StringifyObject(obj: any, ignoreEmptyProps: boolean = false) {
        const copyObj = { ...obj };

        //remove empty props
        if (ignoreEmptyProps) {
            for (const propName in copyObj) {
                if (copyObj[propName] === null || copyObj[propName] === undefined || copyObj[propName] === '') {
                    delete copyObj[propName];
                }
            }
        }
        return JSON.stringify(copyObj);
    }

    public static DeepProxy(obj: any, subject: Subject<void> = null) {
        const self = this;
        const mapStore = {};
        return new Proxy(obj, {
            get(target, property) {
                const value = target[property];
                // if this property has been proxied, just return
                if (mapStore[property] === true) return value;
                // if it's an non-proxied object, we return its proxy
                if (value && Utils.IsObject(value) && !(value instanceof Date) || Utils.IsArray(value)) {
                    const proxyValue = mapStore[property] || self.DeepProxy(value, subject);
                    mapStore[property] = proxyValue;
                    return proxyValue;
                }
                // else we just take a mark
                mapStore[property] = true;
                return value;
            },
            set(target, property, value) {
                const newVal = (Utils.IsObject(value) && value && !(value instanceof Date) || Utils.IsArray(value))
                    ? self.DeepProxy(value, subject)
                    : value;
                target[property] = newVal;
                mapStore[property] = true;
                subject && subject.next();
                return true;
            },
            deleteProperty(target, property) {
                delete target[property];
                delete mapStore[property];
                subject && subject.next();
                return true;
            }
        });
    }

    public static IsArray(arr: any) {
        return Array.isArray(arr);
    }

    public static IsObject(obj: any) {
        return typeof obj === 'object';
    }

    public static IsEmail(email: string, isSupportMultiple: boolean = false) {
        if (!email || email.length === 0) {
            return false;
        }

        const validRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        let emails = [];

        if (isSupportMultiple) {
            emails = email.split(';');
        } else {
            emails.push(email);
        }

        for (let i = 0; i < emails.length; i++) {
            if (!emails[i].match(validRegex)) {
                return false;
            }
        }

        return true;
    }

    public static GetIconStyle(extension: string) {
        if (extension) {
            switch (extension) {
                case '.pdf':
                    return 'mdi mdi-file-pdf-box';
                case '.doc':
                case '.docx':
                    return 'mdi mdi-file-word-box';
                case '.xls':
                case '.xlsx':
                    return 'mdi mdi-file-excel-box';
                case '.rft':
                case '.txt':
                    return 'mdi mdi-text-box';
                case '.zip':
                case '.rar':
                case '.gzip':
                    return 'mdi mdi-zip-box';
                case '.htm':
                case '.html':
                    return 'mdi mdi-language-html5';
                case '.jpg':
                case '.jpeg':
                    return 'mdi mdi-file-jpg-box';
                case '.gif':
                    return 'mdi mdi-file-gif-box';
                case '.bmp':
                case '.tiff':
                case '.png':
                    return 'mdi mdi-file-png-box';
                case '.xml':
                    return 'mdi mdi-file-xml-box';
                default:
                    return 'mdi mdi-file';
            }
        }

        return 'mdi mdi-file';
    }

    public static Round(num: number) {
        return +(Math.round((num + 'e+2') as any) + 'e-2');
    }

    public static SearchTree(element: any, value: any, key = 'key') {
        if (!element) {
            return null;
        }
        if (element[key] == value) {
            return element;
        }
        if (element.items != null) {
            let result = null;
            for (let i = 0; result == null && i < element.items.length; i++) {
                result = Utils.SearchTree(element.items[i], value, key);
            }
            return result;
        }
        return null;
    }

    public static FilterTree = (childNodes: any[], property: string, value: any) => {
        const deepCloneArr = Utils.CloneObject(childNodes);
        return deepCloneArr.filter((object) => {

            const props = property.split(',');
            for (let i = 0; i < props.length; i++) {
                const prop = props[i];
                if (object[prop] && object[prop].toString().toLowerCase().includes(value?.toLowerCase())) {
                    return true;
                }
            }

            if (object.items) {
                object.items = Utils.FilterTree(object.items, property, value);
                return object.items.length;
            }

            return false;
        });
    };

    public static IsIconExists(path: string): boolean {
        const xhr = new XMLHttpRequest();
        xhr.open('HEAD', path, false);
        xhr.send();

        const contentType = xhr.getResponseHeader('Content-Type');
        return xhr.readyState === 4 && xhr.status === 200 && (contentType.startsWith('image/png') || contentType.startsWith('image/svg'));
    }

    public static GroupBy<T, K>(list: T[], keyGetter: (x: T) => K) {
        const map = new Map<K, T[]>();
        list.forEach((item) => {
            const key = keyGetter(item);
            const collection = map.get(key);
            if (!collection) {
                map.set(key, [item]);
            } else {
                collection.push(item);
            }
        });
        return map;
    }

    public static ConvertBase64ToString(str: string) {
        if (!str) return str;
        return window.atob(str);
    }
}
